import {Workbook} from 'exceljs'
import {saveAs} from 'file-saver'

export function isFactPeak(row) {
    return Number(row.old_power) === 4
}

export function isPlanPeak(row) {
    return [3, 4].includes(Number(row.new_power))
}

/** Часы максимума (new_power / old_power) с расчётной почасовки на профиль Энергосферы. */
export function stampPeakFlags(rows, peakSeries) {
    const byKey = rows instanceof Map ? rows : new Map(rows.map((r) => [r.dat_out, r]))
    for (const r of peakSeries || []) {
        const p = moscowParts(r.dat_out)
        if (!p) continue
        const bucket = byKey.get(`${p.dayKey}T${pad2(p.hour)}:00:00`)
        if (!bucket) continue
        bucket.new_power = r.new_power
        bucket.old_power = r.old_power
    }
}

const MSK_OFFSET_MS = 3 * 60 * 60 * 1000

function pad2(n) {
    return String(n).padStart(2, '0')
}

/**
 * Календарный день и час по Москве (UTC+3).
 * Иначе из‑за Z/+00 часть точек уезжает в другой день/час — итог Excel ≠ сумме под графиком.
 */
function moscowParts(datOut) {
    const s = String(datOut ?? '').trim()
    const dateOnly = s.match(/^(\d{4})-(\d{2})-(\d{2})$/)
    if (dateOnly) return {dayKey: s, hour: 0}

    const wall = s.match(/^(\d{4})-(\d{2})-(\d{2})[T\s](\d{2}):(\d{2})/)
    const hasTz = /\d{2}:\d{2}(:\d{2}(\.\d+)?)?(Z|[+-]\d{2}(:?\d{2})?)$/i.test(s)

    if (wall && !hasTz) {
        return {dayKey: `${wall[1]}-${wall[2]}-${wall[3]}`, hour: Number(wall[4])}
    }

    const d = new Date(datOut)
    if (Number.isNaN(d.getTime())) {
        if (wall) return {dayKey: `${wall[1]}-${wall[2]}-${wall[3]}`, hour: Number(wall[4])}
        return null
    }
    const msk = new Date(d.getTime() + MSK_OFFSET_MS)
    return {
        dayKey: `${msk.getUTCFullYear()}-${pad2(msk.getUTCMonth() + 1)}-${pad2(msk.getUTCDate())}`,
        hour: msk.getUTCHours(),
    }
}

/** YYYY-MM-DD (Москва). */
function dayKey(datOut) {
    return moscowParts(datOut)?.dayKey || ''
}

function hourOf(datOut) {
    const p = moscowParts(datOut)
    return p ? p.hour : null
}

const MONTHS_RU = [
    'январь', 'февраль', 'март', 'апрель', 'май', 'июнь',
    'июль', 'август', 'сентябрь', 'октябрь', 'ноябрь', 'декабрь',
]

function eachDayKey(from, to) {
    const out = []
    const d = new Date(from.getFullYear(), from.getMonth(), from.getDate())
    const end = new Date(to.getFullYear(), to.getMonth(), to.getDate())
    while (d <= end) {
        const y = d.getFullYear()
        const m = String(d.getMonth() + 1).padStart(2, '0')
        const day = String(d.getDate()).padStart(2, '0')
        out.push(`${y}-${m}-${day}`)
        d.setDate(d.getDate() + 1)
    }
    return out
}

function hourLabel(h) {
    const a = `${String(h).padStart(2, '0')}.00`
    const b = h === 23 ? '24.00' : `${String(h + 1).padStart(2, '0')}.00`
    return `${a} - ${b}`
}

/** Excel с формата «0.###» показывает целое с хвостовым разделителем («120,»), поэтому целым — «0». */
export function qtyNumFmt(v) {
    return Math.round(Number(v) * 1000) % 1000 === 0 ? '0' : '0.###'
}

const fillHoliday = {type: 'pattern', pattern: 'solid', fgColor: {argb: 'FFFFC0CB'}}
const fillPlan = {type: 'pattern', pattern: 'solid', fgColor: {argb: 'FFC8E6C9'}}
const fillFact = {type: 'pattern', pattern: 'solid', fgColor: {argb: 'FFFFF59D'}}
const fillItogo = {type: 'pattern', pattern: 'solid', fgColor: {argb: 'FFBDD7EE'}}
const fillTotalRow = {type: 'pattern', pattern: 'solid', fgColor: {argb: 'FFFFE0B2'}}
const fillKpiHeader = {type: 'pattern', pattern: 'solid', fgColor: {argb: 'FF5B9BD5'}}
const thinBorder = {
    top: {style: 'thin', color: {argb: 'FF000000'}},
    left: {style: 'thin', color: {argb: 'FF000000'}},
    bottom: {style: 'thin', color: {argb: 'FF000000'}},
    right: {style: 'thin', color: {argb: 'FF000000'}},
}

/** Выходной/праздник: tc_holidays.kod_type_day > 0 */
function isDayOff(dayKeyStr, holidaySet) {
    return holidaySet.has(dayKeyStr)
}

/** KPI по ТЗ комментария G398 */
export function calcPowerKpis(series) {
    const rows = series || []
    const energy = rows.reduce((s, r) => s + (Number(r.out) || 0), 0)

    const factRows = rows.filter(isFactPeak)
    const powerFact = factRows.length
        ? Math.round(factRows.reduce((s, r) => s + (Number(r.out) || 0), 0) / factRows.length)
        : 0

    const byDay = new Map()
    for (const r of rows) {
        if (!isPlanPeak(r)) continue
        const k = dayKey(r.dat_out)
        if (!byDay.has(k)) byDay.set(k, [])
        byDay.get(k).push(Number(r.out) || 0)
    }
    let powerNet = 0
    if (byDay.size > 0) {
        let sumMax = 0
        for (const vals of byDay.values()) {
            sumMax += Math.max(...vals)
        }
        powerNet = Math.round(sumMax / byDay.size)
    }

    const peak = rows.length ? Math.round(Math.max(...rows.map((r) => Number(r.out) || 0))) : 0

    return {energy, powerFact, powerNet, peak}
}

function addHourlySheet(wb, sheetName, seriesRows, {period1, period2, holidays, subtitle, kpis}) {
    const holidaySet = new Set(
        (holidays || [])
            .filter((h) => Number(h.kod_type_day) > 0)
            .map((h) => dayKey(h.d) || String(h.d).slice(0, 10))
            .filter(Boolean)
    )
    const days = eachDayKey(period1, period2)
    const daySet = new Set(days)
    const hours = Array.from({length: 24}, (_, i) => i)
    const singleMonth =
        period1.getFullYear() === period2.getFullYear() &&
        period1.getMonth() === period2.getMonth()

    const energyTotal = seriesRows.reduce((s, r) => s + (Number(r.out) || 0), 0)

    const seriesByDayHour = new Map()
    for (const r of seriesRows) {
        const dk = dayKey(r.dat_out)
        const h = hourOf(r.dat_out)
        if (h == null || h < 0 || h > 23 || !dk || !daySet.has(dk)) continue
        const key = `${dk}|${h}`
        const prev = seriesByDayHour.get(key)
        if (prev) {
            prev.out = (Number(prev.out) || 0) + (Number(r.out) || 0)
            if (r.new_power != null) prev.new_power = Math.max(Number(prev.new_power) || 0, Number(r.new_power) || 0)
            if (r.old_power != null) prev.old_power = Math.max(Number(prev.old_power) || 0, Number(r.old_power) || 0)
        } else {
            seriesByDayHour.set(key, {
                ...r,
                out: Number(r.out) || 0,
            })
        }
    }

    const ws = wb.addWorksheet(sheetName, {
        views: [{state: 'frozen', xSplit: 1, ySplit: 3}],
    })

    const title = singleMonth
        ? `Почасовой расход электроэнергии за ${MONTHS_RU[period1.getMonth()]} ${period1.getFullYear()}`
        : `Почасовой расход электроэнергии за ${MONTHS_RU[period1.getMonth()]} ${period1.getFullYear()} – ${MONTHS_RU[period2.getMonth()]} ${period2.getFullYear()}`

    const colCount = days.length + 2
    ws.mergeCells(1, 1, 1, Math.min(colCount, 12))
    const titleCell = ws.getCell(1, 1)
    titleCell.value = title
    titleCell.font = {bold: true, size: 14, name: 'Arial'}
    titleCell.alignment = {horizontal: 'left', vertical: 'middle', wrapText: true}
    ws.getRow(1).height = 22

    ws.mergeCells(2, 1, 2, Math.min(colCount, 12))
    const subCell = ws.getCell(2, 1)
    subCell.value = subtitle
    subCell.font = {size: 11, name: 'Arial'}

    const gridFont = {name: 'Arial', size: 8}
    const gridFontBold = {name: 'Arial', size: 8, bold: true}
    const valueAlign = {horizontal: 'right', vertical: 'middle'}

    const headerRowIdx = 3
    const headerRow = ws.getRow(headerRowIdx)
    headerRow.height = 30
    headerRow.getCell(1).value = 'время (московское)'
    days.forEach((d, idx) => {
        const cell = headerRow.getCell(idx + 2)
        if (singleMonth) {
            cell.value = Number(d.slice(8, 10))
        } else {
            const [, m, day] = d.split('-')
            cell.value = `${day}.${m}`
        }
        cell.alignment = {horizontal: 'center', vertical: 'middle'}
        if (isDayOff(d, holidaySet)) cell.fill = fillHoliday
        cell.border = thinBorder
        cell.font = gridFontBold
    })
    const itogoHeader = headerRow.getCell(days.length + 2)
    itogoHeader.value = 'ИТОГО'
    itogoHeader.fill = fillItogo
    itogoHeader.font = {...gridFontBold, color: {argb: 'FF0070C0'}}
    itogoHeader.alignment = {horizontal: 'center', vertical: 'middle'}
    itogoHeader.border = thinBorder
    headerRow.getCell(1).border = thinBorder
    headerRow.getCell(1).font = gridFontBold
    headerRow.getCell(1).alignment = {horizontal: 'center', vertical: 'middle', wrapText: true}

    for (const h of hours) {
        const row = ws.addRow([])
        const rowIdx = row.number
        const hourCell = ws.getCell(rowIdx, 1)
        hourCell.value = hourLabel(h)
        hourCell.border = thinBorder
        hourCell.font = gridFont
        hourCell.alignment = {horizontal: 'center', vertical: 'middle'}

        let hourSum = 0
        days.forEach((d, idx) => {
            const cellData = seriesByDayHour.get(`${d}|${h}`)
            const v = cellData ? Number(cellData.out) || 0 : 0
            hourSum += v
            const excelCell = ws.getCell(rowIdx, idx + 2)
            excelCell.value = v
            excelCell.numFmt = qtyNumFmt(v)
            excelCell.border = thinBorder
            excelCell.alignment = valueAlign
            excelCell.font = gridFont

            if (isDayOff(d, holidaySet)) excelCell.fill = fillHoliday
            if (cellData && isPlanPeak(cellData)) excelCell.fill = fillPlan
            if (cellData && isFactPeak(cellData)) excelCell.fill = fillFact
        })

        const sumCell = ws.getCell(rowIdx, days.length + 2)
        sumCell.value = hourSum
        sumCell.numFmt = qtyNumFmt(hourSum)
        sumCell.fill = fillItogo
        sumCell.font = {...gridFontBold, color: {argb: 'FF0070C0'}}
        sumCell.border = thinBorder
        sumCell.alignment = valueAlign
    }

    const totalRow = ws.addRow([])
    const totalRowIdx = totalRow.number
    const totalLabel = ws.getCell(totalRowIdx, 1)
    totalLabel.value = 'Общий итог'
    totalLabel.fill = fillTotalRow
    totalLabel.font = gridFontBold
    totalLabel.border = thinBorder
    totalLabel.alignment = {horizontal: 'center', vertical: 'middle'}

    days.forEach((d, idx) => {
        let s = 0
        for (const h of hours) {
            const cellData = seriesByDayHour.get(`${d}|${h}`)
            s += cellData ? Number(cellData.out) || 0 : 0
        }
        const excelCell = ws.getCell(totalRowIdx, idx + 2)
        excelCell.value = s
        excelCell.numFmt = qtyNumFmt(s)
        excelCell.fill = fillTotalRow
        excelCell.font = gridFontBold
        excelCell.border = thinBorder
        excelCell.alignment = valueAlign
    })
    const grandCell = ws.getCell(totalRowIdx, days.length + 2)
    grandCell.value = energyTotal
    grandCell.numFmt = qtyNumFmt(energyTotal)
    grandCell.fill = fillItogo
    grandCell.font = {...gridFontBold, color: {argb: 'FF0070C0'}}
    grandCell.border = thinBorder
    grandCell.alignment = valueAlign

    // Колонка «00.00 - 01.00» по ширине подписи, дни — чтобы число не схлопывалось в ####.
    ws.getColumn(1).width = 16
    for (let c = 2; c <= days.length + 1; c++) ws.getColumn(c).width = 9
    ws.getColumn(days.length + 2).width = 12

    const kpiLabelFrom = 2
    const kpiLabelTo = 7
    const kpiValueCol = 8
    const kpiStart = totalRowIdx + 2
    ws.mergeCells(kpiStart, kpiLabelFrom, kpiStart, kpiValueCol)
    for (let c = kpiLabelFrom; c <= kpiValueCol; c++) {
        const cell = ws.getCell(kpiStart, c)
        cell.fill = fillKpiHeader
        cell.border = thinBorder
        cell.font = {...gridFontBold, color: {argb: 'FFFFFFFF'}}
    }
    const kpiHead = ws.getCell(kpiStart, kpiLabelFrom)
    kpiHead.value = 'Основные показатели'
    kpiHead.alignment = {horizontal: 'center', vertical: 'middle'}

    const kpiRows = [
        ['Потребление электроэнергии, кВтч', energyTotal],
        ['Мощность фактическая, кВт', kpis.powerFact],
        ['Мощность сетевая, кВт', kpis.powerNet],
        ['Пиковое значение мощности, кВт', kpis.peak],
    ]
    kpiRows.forEach(([label, val], i) => {
        const r = kpiStart + 1 + i
        ws.mergeCells(r, kpiLabelFrom, r, kpiLabelTo)
        for (let c = kpiLabelFrom; c <= kpiValueCol; c++) {
            const cell = ws.getCell(r, c)
            cell.border = thinBorder
            cell.font = gridFont
            cell.alignment = {vertical: 'middle', horizontal: c === kpiValueCol ? 'right' : 'left'}
        }
        const nameCell = ws.getCell(r, kpiLabelFrom)
        nameCell.value = label
        const valueCell = ws.getCell(r, kpiValueCol)
        valueCell.font = gridFontBold
        if (val == null) {
            valueCell.value = '—'
        } else {
            valueCell.value = Number(val) || 0
            valueCell.numFmt = i === 0 ? qtyNumFmt(valueCell.value) : '0'
        }
    })
    ws.getColumn(kpiValueCol).width = Math.max(ws.getColumn(kpiValueCol).width || 8, 12)
}

/** Файл «Анализ потребления»: лист почасовки по точкам и/или лист суммарного профиля договора. */
export async function exportConsumptionExcel({series = [], dogSeries = [], holidays, period1, period2, ndog, subtitle = ''}) {
    const wb = new Workbook()
    const sheetOpts = {period1, period2, holidays, subtitle}
    if (series.length) {
        addHourlySheet(wb, 'Почасовка', series, {...sheetOpts, kpis: calcPowerKpis(series)})
    }
    if (dogSeries.length) {
        const dogSubtitle = subtitle
            ? subtitle + ' — Суммарный профиль по договору'
            : 'Суммарный профиль по договору'
        const dogKpis = calcPowerKpis(dogSeries)
        const dogHasPeaks = dogSeries.some((r) => r.new_power != null || r.old_power != null)
        addHourlySheet(wb, 'Суммарный профиль', dogSeries, {
            ...sheetOpts,
            subtitle: dogSubtitle,
            kpis: dogHasPeaks ? dogKpis : {...dogKpis, powerFact: null, powerNet: null},
        })
    }
    const buf = await wb.xlsx.writeBuffer()
    const d1 = period1.toLocaleDateString('ru-RU')
    const d2 = period2.toLocaleDateString('ru-RU')
    saveAs(new Blob([buf]), `Анализ потребления №${ndog} ${d1}-${d2}.xlsx`)
}
